import type { ChatEntry } from './workflow-editor-state';

import { memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { alpha } from '@mui/material/styles';
import { Box, Stack, Button, Typography, CircularProgress } from '@mui/material';

import { boldSpans, replyParts } from './reply-parts';
import type { LucideIcon } from '../../../components/lucide-icon';

import { Sparkles, CircleCheck } from '../../../components/lucide-icon';

/** Ari's mark: the brand gradient behind the sparkles, as the chat panel header shows it too. */
export function AriMark({ size = 40, icon: Icon = Sparkles }: { size?: number; icon?: LucideIcon }) {
  return (
    <Box
      sx={(theme) => ({
        width: size,
        height: size,
        flexShrink: 0,
        display: 'grid',
        placeItems: 'center',
        borderRadius: size >= 40 ? 2 : 1.5,
        color: 'common.white',
        background: theme.gradients?.primary.background ?? theme.palette.primary.main,
      })}
    >
      <Icon size={Math.round(size * 0.5)} />
    </Box>
  );
}

/** Shown while Ari works on a turn. */
export function AriWorking() {
  const { t } = useTranslation();
  return (
    <Stack direction="row" spacing={1.25} alignItems="center" data-testid="workflow.chat.working">
      <AriMark size={24} />
      <CircularProgress size={14} sx={{ color: 'text.secondary' }} />
      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
        {t('skills.workflow.chat.working')}
      </Typography>
    </Stack>
  );
}

export interface AriEntryProps {
  entry: ChatEntry;
  /** The reply the author is reading now. A question it ends on waits for their answer. */
  waiting: boolean;
  /** Offers to undo the change this reply made. Left out where nothing can be undone. */
  onUndo?: () => void;
  /** Offers to apply the changes this Level up reply proposes. */
  onApply?: () => void;
}

/** One message from Ari: its text with lists laid out, the question it asks, and what it changed. */
export const AriEntry = memo(function AriEntry({ entry, waiting, onUndo, onApply }: AriEntryProps) {
  const { t } = useTranslation();
  // A keystroke in the box redraws the chat, so the parse runs only when the reply changes.
  const parts = useMemo(() => replyParts(entry.text), [entry.text]);
  const textSx = { fontSize: 15, lineHeight: 1.6, color: entry.failed ? 'error.light' : 'text.primary' } as const;
  return (
    <Stack direction="row" spacing={1.25} alignItems="flex-start" data-testid="workflow.chat.ari">
      <AriMark size={24} />
      <Stack spacing={1} sx={{ minWidth: 0, flex: 1, pt: 0.25 }}>
        {parts.blocks.map((block, i) =>
          block.kind === 'text' ? (
            <Typography key={i} sx={textSx}>
              <Spans text={block.text} />
            </Typography>
          ) : (
            <Box
              key={i}
              component="ul"
              sx={{ m: 0, pl: 2.5, listStyleType: 'disc', '& li + li': { mt: 0.5 }, ...textSx }}
            >
              {block.items.map((item) => (
                <li key={item}>
                  <Spans text={item} />
                </li>
              ))}
            </Box>
          )
        )}
        {parts.question &&
          (waiting ? (
            <Box
              data-testid="workflow.chat.question"
              sx={(theme) => ({
                px: 1.5,
                py: 1.25,
                borderRadius: 1.5,
                border: 1,
                borderColor: alpha(theme.palette.info.main, 0.4),
                bgcolor: alpha(theme.palette.info.main, 0.1),
              })}
            >
              <Typography variant="overline" sx={{ color: 'info.light', lineHeight: 1.6 }}>
                {t('skills.workflow.chat.asks')}
              </Typography>
              <Typography sx={{ ...textSx, fontWeight: 'fontWeightMedium' }}>
                <Spans text={parts.question} />
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                {t('skills.workflow.chat.answerBelow')}
              </Typography>
            </Box>
          ) : (
            <Typography sx={textSx}>
              <Spans text={parts.question} />
            </Typography>
          ))}
        {waiting && parts.asks && !parts.question && (
          <Typography
            variant="caption"
            data-testid="workflow.chat.answer-hint"
            sx={(theme) => ({
              alignSelf: 'flex-start',
              px: 1,
              py: 0.25,
              borderRadius: 1,
              color: 'info.light',
              bgcolor: alpha(theme.palette.info.main, 0.1),
            })}
          >
            {t('skills.workflow.chat.answerBelow')}
          </Typography>
        )}
        {onApply && (
          <Button
            size="small"
            variant="contained"
            onClick={onApply}
            data-testid="workflow.chat.apply"
            sx={{ alignSelf: 'flex-start' }}
          >
            {t('skills.workflow.memories.apply')}
          </Button>
        )}
        {entry.applied && (
          <Stack direction="row" spacing={0.75} alignItems="center" data-testid="workflow.chat.updated">
            <CircleCheck size={14} />
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              {t('skills.workflow.chat.updated')}
            </Typography>
            {onUndo && (
              <Button
                size="small"
                onClick={onUndo}
                data-testid="workflow.chat.undo"
                sx={{ minWidth: 0, py: 0, px: 0.75 }}
              >
                {t('skills.workflow.chat.undo')}
              </Button>
            )}
          </Stack>
        )}
      </Stack>
    </Stack>
  );
});

function Spans({ text }: { text: string }) {
  return (
    <>
      {boldSpans(text).map((span, i) =>
        span.bold ? (
          <Box key={i} component="strong" sx={{ fontWeight: 'fontWeightSemiBold' }}>
            {span.text}
          </Box>
        ) : (
          span.text
        )
      )}
    </>
  );
}
